import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import FollowService, { getBlockStatus } from '../services/follow-service';
import { FollowStatus, FollowButtonState } from '../types/follow';
import { logger } from '../utils/logger';

interface UseFollowButtonProps {
  targetUserId: string;
  initialFollowStatus?: FollowStatus;
}

interface UseFollowButtonReturn {
  followStatus: FollowStatus | null;
  blockStatus: { isBlocked: boolean; isBlockedBy: boolean } | null;
  buttonState: FollowButtonState;
  handleFollowAction: () => Promise<void>;
  shouldRender: boolean;
}

/**
 * Centralized hook for follow button logic
 * Ensures all follow buttons use the same consistent logic
 */
export const useFollowButton = ({
  targetUserId,
  initialFollowStatus,
}: UseFollowButtonProps): UseFollowButtonReturn => {
  const { user: currentUser } = useAuth();
  const [followStatus, setFollowStatus] = useState<FollowStatus | null>(initialFollowStatus || null);
  const [blockStatus, setBlockStatus] = useState<{ isBlocked: boolean; isBlockedBy: boolean } | null>(null);
  const [buttonState, setButtonState] = useState<FollowButtonState>({
    isLoading: false,
    isFollowing: false,
    buttonText: 'Follow',
    buttonColor: 'primary',
    disabled: false,
  });

  // Determine if button should render (not for same user)
  // ENHANCED SHOULDRENDER LOGGING
  const shouldRender = !!(currentUser && currentUser.id !== targetUserId);
  
  // Enhanced logging for shouldRender calculation
  console.log('🎯 useFollowButton shouldRender calculation:', {
    hasCurrentUser: !!currentUser,
    currentUserId: currentUser?.id,
    targetUserId: targetUserId,
    idsAreEqual: currentUser?.id === targetUserId,
    shouldRender: shouldRender,
    calculation: `shouldRender = !!(currentUser && currentUser.id !== targetUserId)`
  });

  // Fetch follow status and block status if not provided
  useEffect(() => {
    console.log('🔍 useFollowButton: useEffect triggered');
    console.log('  Current User:', currentUser?.id, currentUser?.displayName);
    console.log('  Target User ID:', targetUserId);
    console.log('  Initial Follow Status:', initialFollowStatus);
    console.log('  Should fetch?', !!(currentUser?.id && targetUserId && !initialFollowStatus));

    const fetchStatuses = async () => {
      console.log('🔍 useFollowButton: Starting status fetch');

      if (initialFollowStatus) {
        console.log('✅ useFollowButton: Using initial follow status, skipping fetch');
        return;
      }

      // Don't fetch if we don't have a current user or target user
      if (!currentUser?.id || !targetUserId) {
        console.log('⚠️ useFollowButton: Missing current user or target user, skipping fetch');
        return;
      }

      try {
        setButtonState(prev => ({ ...prev, isLoading: true }));
        console.log('🔄 useFollowButton: Fetching follow status and block status...');

        // Fetch both follow status and block status
        const [followStatusResult, blockStatusResult] = await Promise.all([
          FollowService.getFollowStatus(targetUserId),
          getBlockStatus(targetUserId),
        ]);

        console.log('📊 useFollowButton: API Results received');
        console.log('  Follow Status Result:', followStatusResult);
        console.log('  Block Status Result:', blockStatusResult);

        setFollowStatus(followStatusResult);
        setBlockStatus(blockStatusResult);
      } catch (error) {
        console.error('❌ useFollowButton: Failed to fetch statuses:', error);
        logger.error('Failed to fetch statuses:', error);
      } finally {
        setButtonState(prev => ({ ...prev, isLoading: false }));
      }
    };

    // Only fetch if we have all required data and should render
    if (shouldRender && currentUser?.id && targetUserId && !initialFollowStatus) {
      fetchStatuses();
    }
  }, [targetUserId, initialFollowStatus, currentUser?.id, shouldRender]);

  // Update button state based on follow status and block status
  useEffect(() => {
    console.log('🎯 useFollowButton: Updating button state');
    console.log('  Follow Status:', followStatus);
    console.log('  Block Status:', blockStatus);

    if (!followStatus) {
      console.log('⚠️ useFollowButton: No follow status available, skipping state update');
      return;
    }

    const newState: Partial<FollowButtonState> = {
      isFollowing: followStatus.isFollowing,
    };

    console.log('📊 useFollowButton: Processing relationship status');
    console.log('  isFollowing:', followStatus.isFollowing);
    console.log('  isFollowedBy:', followStatus.isFollowedBy);
    console.log('  relationship:', followStatus.relationship);

    // Check if blocked - disable button if blocked
    if (blockStatus?.isBlocked || blockStatus?.isBlockedBy) {
      newState.buttonText = blockStatus.isBlocked ? 'Blocked' : 'Blocked by user';
      newState.buttonColor = 'error';
      newState.disabled = true;
      console.log('🚫 useFollowButton: User is blocked, button disabled');
    } else {
      // Normal follow status handling
      console.log('🔄 useFollowButton: Processing relationship:', followStatus.relationship);

      switch (followStatus.relationship) {
        case 'following':
          newState.buttonText = 'Following';
          newState.buttonColor = 'secondary';
          newState.disabled = false;
          console.log('✅ useFollowButton: Set to "Following" (case: following)');
          break;
        case 'mutual':
          newState.buttonText = 'Following';
          newState.buttonColor = 'secondary';
          newState.disabled = false;
          console.log('✅ useFollowButton: Set to "Following" (case: mutual)');
          break;
        case 'pending':
          newState.buttonText = 'Pending';
          newState.buttonColor = 'warning';
          newState.disabled = false;
          console.log('⏳ useFollowButton: Set to "Pending" (case: pending)');
          break;
        case 'follower':
          newState.buttonText = 'Follow Back';
          newState.buttonColor = 'primary';
          newState.disabled = false;
          console.log('🔄 useFollowButton: Set to "Follow Back" (case: follower)');
          break;
        case 'none':
        default:
          newState.buttonText = 'Follow';
          newState.buttonColor = 'primary';
          newState.disabled = false;
          console.log('➕ useFollowButton: Set to "Follow" (case: none/default)');
          break;
      }
    }

    console.log('🎯 useFollowButton: Final button state:', newState);
    setButtonState(prev => ({ ...prev, ...newState }));
  }, [followStatus, blockStatus]);

  // Handle follow/unfollow action
  const handleFollowAction = async () => {
    if (!followStatus || buttonState.isLoading) {
      return;
    }

    try {
      setButtonState(prev => ({ ...prev, isLoading: true }));

      if (followStatus.isFollowing || followStatus.requestStatus === 'pending') {
        // Unfollow or cancel request
        await FollowService.unfollowUser(targetUserId);

        const newStatus: FollowStatus = {
          isFollowing: false,
          isFollowedBy: followStatus.isFollowedBy,
          relationship: followStatus.isFollowedBy ? 'follower' : 'none',
        };

        setFollowStatus(newStatus);
        logger.info(`Successfully unfollowed user: ${targetUserId}`);
      } else {
        // Follow user
        const result = await FollowService.followUser(targetUserId);

        const newStatus: FollowStatus = {
          isFollowing: !result.requiresApproval,
          isFollowedBy: followStatus.isFollowedBy,
          requestStatus: result.requiresApproval ? 'pending' : undefined,
          relationship: result.requiresApproval ? 'pending' :
            followStatus.isFollowedBy ? 'mutual' : 'following',
        };

        setFollowStatus(newStatus);
        logger.info(`Successfully followed user: ${targetUserId}`, { requiresApproval: result.requiresApproval });
      }
    } catch (error: any) {
      logger.error('Follow action failed:', error);
      // TODO: Show error toast/snackbar
    } finally {
      setButtonState(prev => ({ ...prev, isLoading: false }));
    }
  };

  return {
    followStatus,
    blockStatus,
    buttonState,
    handleFollowAction,
    shouldRender,
  };
};